import {
  affinityMultiplier,
  resolveAffinity,
  type Element,
  type ElementAffinity,
} from '../game/elements';
import type { PlayerAttributes } from '../game/types';
import type { ActiveStatus } from './statuses';
import { getStatus, hasElementalImmunity, hasStatus } from './statuses';

export type DamageBreakdown = {
  base: number;
  scaling: number;
  affinity: ElementAffinity;
  multiplier: number;
  finalDamage: number;
};

/**
 * Physical → strength; elemental magic → intelligence (gameplay_ideas).
 * Apply weak/resist/immune multipliers.
 */
export function computeSkillDamage(
  element: Element,
  power: number,
  stats: PlayerAttributes,
  weak: readonly Element[] = [],
  resist: readonly Element[] = [],
  immune: readonly Element[] = [],
): DamageBreakdown {
  const scaling =
    element === 'physical'
      ? Math.floor(stats.strength / 2)
      : Math.floor(stats.intelligence / 2);

  const base = power + scaling;
  const affinity = resolveAffinity(element, weak, resist, immune);
  const multiplier = affinityMultiplier(affinity);
  const finalDamage = Math.floor(base * multiplier);

  return { base, scaling, affinity, multiplier, finalDamage };
}

export interface ModifiedDamageBreakdown extends DamageBreakdown {
  affinityDamage: number;
  powerUpMultiplier: number;
  receivedDamageMultiplier: number;
  blockedByStatus: boolean;
}

/** Shared preview/execution formula including active player/enemy statuses. */
export function computeModifiedSkillDamage(
  element: Element,
  power: number,
  stats: PlayerAttributes,
  weak: readonly Element[] = [],
  resist: readonly Element[] = [],
  immune: readonly Element[] = [],
  playerStatuses: readonly ActiveStatus[] = [],
  enemyStatuses: readonly ActiveStatus[] = [],
): ModifiedDamageBreakdown {
  const baseDamage = computeSkillDamage(element, power, stats, weak, resist, immune);
  const powerUpMultiplier = getStatus(playerStatuses, 'power_up')?.potency ?? 1;
  const receivedDamageMultiplier = getStatus(enemyStatuses, 'weaken')?.potency ?? 1;
  const blockedByStatus =
    hasStatus(enemyStatuses, 'invulnerable') ||
    hasElementalImmunity(enemyStatuses, element);

  let finalDamage = blockedByStatus ? 0 : baseDamage.finalDamage;
  finalDamage = Math.floor(finalDamage * powerUpMultiplier);
  finalDamage = Math.floor(finalDamage * receivedDamageMultiplier);

  return {
    ...baseDamage,
    affinityDamage: baseDamage.finalDamage,
    powerUpMultiplier,
    receivedDamageMultiplier,
    blockedByStatus,
    finalDamage,
  };
}

export function affinityHint(affinity: ElementAffinity): string {
  switch (affinity) {
    case 'weak':
      return ' (chí mạng thuộc tính!)';
    case 'resist':
      return ' (bị kháng…)';
    case 'immune':
      return ' (miễn nhiễm — 0 sát thương)';
    default:
      return '';
  }
}
